import { Point2 } from '../../../../domain/geometry/geometric-types';
import { kilerMap } from '../../ProductChoices';
import { Diamond, KileTypeSKU, WedgeCalculationResult } from './wedge-types';
import { kileSelector } from './kile-selector';

// Get the actual length of a wedge type from the kilerMap
export const getWedgeLengthFromType = (type: KileTypeSKU): number => {
  const wedgeProduct = kilerMap[type]?.[0];
  return wedgeProduct?.length || 4.8; // Default to 4.8m if not found
};

// Function to check if a diamond only has wedges on one side
export const onlyOneSide = (diamond?: Diamond): boolean => {
  if (!diamond) return false;

  // If no triangles on left side or no triangles on right side, it's single-sided
  if (!diamond.triangle1 && !diamond.triangle3) return true; // No left side
  if (!diamond.triangle2 && !diamond.triangle4) return true; // No right side

  return false;
};

// Helper function to get kile type from length - directly using imported kileSelector
const kileTypeFromLength = (diamond: Diamond, length: number): KileTypeSKU => {
  return (
    (kileSelector(diamond, length) as KileTypeSKU) || KileTypeSKU.INVALID_KILE
  );
};

// Select the appropriate wedge type based on segment length and diamond configuration
export const selectWedgeType = (
  diamond: Diamond,
  segmentLengthInMeters: number
): KileTypeSKU => {
  // Check if we have single-sided wedges
  const isSingleSided = onlyOneSide(diamond);

  // If we have only one side wedges, use the segment length as is
  // If we have wedges on both sides, the product length is half the segment length
  const effectiveLength = isSingleSided
    ? segmentLengthInMeters
    : segmentLengthInMeters / 2;

  // Use the kile selector to get the appropriate type based on the diamond and length
  const selectedType = kileTypeFromLength(diamond, effectiveLength);
  return selectedType;
};

// Helper function to process wedges with two segments (just end wedges)
export const processTwoSegmentWedges = (
  start: Point2,
  end: Point2,
  pdfDistance: number,
  normalizedDirX: number,
  normalizedDirY: number,
  distanceInMeters: number,
  baseDiamond: Diamond
): WedgeCalculationResult => {
  const points: Point2[] = [start];
  const diamonds: Diamond[] = [];
  const kileTypes: KileTypeSKU[] = [];
  const segmentLengths: number[] = [];

  // For just two wedges, use the midpoint
  const midPoint: Point2 = [
    start[0] + normalizedDirX * (pdfDistance / 2),
    start[1] + normalizedDirY * (pdfDistance / 2),
  ];
  points.push(midPoint, end);

  // First wedge: triangle1 and triangle3 should be false (only second triangles visible)
  const firstWedgeDiamond: Diamond = {
    ...baseDiamond,
    triangle1: false,
    triangle3: false,
  };
  diamonds.push(firstWedgeDiamond);

  // Last wedge: triangle2 and triangle4 should be false (only first triangles visible)
  const lastWedgeDiamond: Diamond = {
    ...baseDiamond,
    triangle2: false,
    triangle4: false,
  };
  diamonds.push(lastWedgeDiamond);

  // Calculate lengths for the two segments
  // Round to 2 decimal places for consistency
  const distanceInMetersRounded = Math.round(distanceInMeters * 100) / 100;

  // Determine wedge types based on segment lengths and diamond configuration
  const firstWedgeType = selectWedgeType(
    firstWedgeDiamond,
    distanceInMetersRounded
  );
  const secondWedgeType = selectWedgeType(
    lastWedgeDiamond,
    distanceInMetersRounded
  );

  kileTypes.push(firstWedgeType, secondWedgeType);
  segmentLengths.push(distanceInMetersRounded, distanceInMetersRounded);

  return { points, kileTypes, diamonds, segmentLengths };
};

// Main function to calculate wedge points between two points
export const calculateWedgePoints = (
  start: Point2,
  end: Point2,
  unitScale: number,
  baseDiamond: Diamond,
  defaultMiddleWedgeType: KileTypeSKU
): WedgeCalculationResult => {
  // Calculate the distance between points in PDF coordinates
  const pdfDistance = Math.sqrt(
    Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
  );

  // Convert to meters and round to 2 decimal places for consistency
  const distanceInMeters = Math.round(pdfDistance * unitScale * 100) / 100;

  // For middle segments, use the current defaultMiddleWedgeType
  const defaultProductLength = getWedgeLengthFromType(defaultMiddleWedgeType);

  // For middle segments, the segment length needs to be twice the product length
  const middleSegmentLength = Math.round(defaultProductLength * 2 * 100) / 100;

  // Calculate how many FULL wedges can fit between the start and end
  // Leave space at ends for partial wedges
  const minEndWedgeLength = 0.5; // minimum length for end wedges in meters
  const fullWedgeCount = Math.max(
    0,
    Math.floor((distanceInMeters - 2 * minEndWedgeLength) / middleSegmentLength)
  );

  // Total wedge count = full wedges + 2 end wedges (if there's space)
  const totalSegmentCount =
    fullWedgeCount > 0
      ? fullWedgeCount + 2
      : Math.min(2, Math.ceil(distanceInMeters / minEndWedgeLength));

  // Handle special case: if there's only space for one segment
  if (totalSegmentCount <= 1) {
    // For a single segment, use a full diamond (all triangles true)
    const singleDiamond: Diamond = {
      triangle1: true,
      triangle2: true,
      triangle3: true,
      triangle4: true,
    };

    // Select wedge type based on length for single wedge
    const wedgeType = selectWedgeType(singleDiamond, distanceInMeters);

    return {
      points: [start, end],
      kileTypes: [wedgeType],
      diamonds: [singleDiamond],
    };
  }

  // Handle case with just two segments (end segments only)
  if (totalSegmentCount === 2) {
    // Calculate direction vector
    const dirX = end[0] - start[0];
    const dirY = end[1] - start[1];

    // Normalize the direction vector
    const normalizedDirX = dirX / pdfDistance;
    const normalizedDirY = dirY / pdfDistance;

    return processTwoSegmentWedges(
      start,
      end,
      pdfDistance,
      normalizedDirX,
      normalizedDirY,
      distanceInMeters,
      baseDiamond
    );
  }

  // Calculate the total length used by full-sized middle segments in meters
  const fullWedgesLengthInMeters =
    Math.round(fullWedgeCount * middleSegmentLength * 100) / 100;

  // Calculate the remaining length to be shared between the two end segments in meters
  const remainingLengthInMeters =
    Math.round((distanceInMeters - fullWedgesLengthInMeters) * 100) / 100;

  // Each end segment gets half of the remaining length (in meters)
  const endSegmentLengthInMeters =
    Math.round((remainingLengthInMeters / 2) * 100) / 100;

  // Convert to PDF units for calculations
  const middleSegmentLengthInPdfUnits = middleSegmentLength / unitScale;
  const endSegmentLengthInPdfUnits = endSegmentLengthInMeters / unitScale;

  // Calculate direction vector
  const dirX = end[0] - start[0];
  const dirY = end[1] - start[1];

  // Normalize the direction vector
  const normalizedDirX = dirX / pdfDistance;
  const normalizedDirY = dirY / pdfDistance;

  // Generate points
  const points: Point2[] = [];
  const kileTypes: KileTypeSKU[] = [];
  const diamonds: Diamond[] = [];

  // Always start at the exact start point
  points.push(start);

  // Second point is at end of first segment (endSegmentLengthInPdfUnits from start)
  const firstSegmentEnd: Point2 = [
    start[0] + normalizedDirX * endSegmentLengthInPdfUnits,
    start[1] + normalizedDirY * endSegmentLengthInPdfUnits,
  ];
  points.push(firstSegmentEnd);

  // First segment: triangle1 and triangle3 should be false (only second triangles visible)
  const firstSegmentDiamond: Diamond = {
    triangle1: false,
    triangle2: true,
    triangle3: false,
    triangle4: true,
  };
  diamonds.push(firstSegmentDiamond);

  // Determine wedge type for first segment based on its length and diamond configuration
  const firstSegmentType = selectWedgeType(
    firstSegmentDiamond,
    endSegmentLengthInMeters
  );
  kileTypes.push(firstSegmentType);

  // Generate points for each full middle segment
  let currentDistance = endSegmentLengthInPdfUnits;

  for (let i = 0; i < fullWedgeCount; i++) {
    currentDistance += middleSegmentLengthInPdfUnits;
    const x = start[0] + normalizedDirX * currentDistance;
    const y = start[1] + normalizedDirY * currentDistance;
    points.push([x, y] as Point2);

    // Middle segments use full diamond configuration (all triangles visible)
    const middleDiamond: Diamond = {
      triangle1: true,
      triangle2: true,
      triangle3: true,
      triangle4: true,
    };
    diamonds.push(middleDiamond);

    // Calculate wedge type for middle segments based on segment length and diamond configuration
    const middleSegmentType = selectWedgeType(
      middleDiamond,
      middleSegmentLength
    );
    kileTypes.push(middleSegmentType);
  }

  // Last point is at the exact end
  points.push(end);

  // Last segment: triangle2 and triangle4 should be false (only first triangles visible)
  const lastSegmentDiamond: Diamond = {
    triangle1: true,
    triangle2: false,
    triangle3: true,
    triangle4: false,
  };
  diamonds.push(lastSegmentDiamond);

  // Determine wedge type for last segment based on its length and diamond configuration
  const lastSegmentType = selectWedgeType(
    lastSegmentDiamond,
    endSegmentLengthInMeters
  );
  kileTypes.push(lastSegmentType);

  return { points, kileTypes, diamonds };
};
